import {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  useReducer,
} from "react";
import { LayoutComponent } from "../LayoutComponent";
import {
  Content,
  ProgressCircle,
  View,
  Text,
  TextField,
  Divider,
  DatePicker,
  Provider,
  ActionButton,
  ToastQueue,
  Button,
  DialogTrigger,
  Dialog,
  Picker,
  Item,
  Heading,
  Content as DialogContent,
  ComboBox,
  useAsyncList,
  Flex,
} from "@adobe/react-spectrum";
import Checkmark from "@spectrum-icons/workflow/Checkmark";
import Close from "@spectrum-icons/workflow/Close";
import GraphBarVertical from "@spectrum-icons/workflow/GraphBarVertical";
import moment from "moment";
import { formatDecimalToString } from "../../../../utils/helperUtil";
import { useDependency } from "../../../../contexts/DependencyProvider";
import { ListProjectItemRequest } from "../../../../services/soap/item/requests/ListProjectItemRequest";
import { ListProjectItemRequestOptions_ItemGrid } from "../../../../services/soap/item/requests/ListProjectItemRequestOptions";
import { ListProjectItemCalculationRequest } from "../../../../services/soap/item/requests/ListProjectItemCalculationRequest";
import { useViewInfo } from "../../../../hooks/UseViewInfo";
import { useTranslation } from "react-i18next";
import { FPAData, FPADataTypes } from "../../../../infra/protected/FPA/FPAData";
import { ListFPARequest2 } from "../../../../services/soap/project/requests/ListFPARequest2";
import { GetItemRequest } from "../../../../services/soap/item/requests/GetItemRequest";
import useComonentReload from "../../../../hooks/UseComponentReload";
import { parseDate } from "@internationalized/date";
import { useDebounce } from "@uidotdev/usehooks";
import { GetProjectRequest } from "../../../../services/soap/project/requests/GetProjectRequest";
import { TreeGridComponent } from "../../../ui/TreeGridComponent/TreeGridComponent";
import {
  TreeGridColumn,
  TreeGridRow,
  ComboBoxItem,
} from "../../../ui/TreeGridComponent/types";
import { ListItemRequest } from "../../../../services/soap/item/requests/ListItemRequest";
import { UpdateProjectRequest } from "../../../../services/soap/project/requests/UpdateProjectRequest";
import { AlertIconFill } from "../../Icons/IconsLib";
import NumericTextField from "../../../ui/NumericTextField";
import { ModifyProjectItemValuesRequest } from "../../../../services/soap/item/requests/ModifyProjectItemValuesRequest";
import { UpdateProjectItemCalculationRequest } from "../../../../services/soap/item/requests/UpdateProjectItemCalculationRequest";
import { UpdateProjectItemRequest } from "../../../../services/soap/item/requests/UpdateProjectItemRequest";
import { ListCurrencyRateRequest } from "../../../../services/soap/finance/requests/ListCurrencyRateRequest";
import { usePreloadAssets } from "../../../../hooks/UsePreloadAssets";
import {
  projectCalculationReducer,
  selectionReducer,
  initialProjectCalculationState,
  initialSelectionState,
} from "./useProjectItemGrid";
import {
  generateBillingItem,
  generateProjectProductItem,
} from "../../../../services/soap/item/requests/CreateProjectItemOptions";
import { CreateInvoicesFromItemsRequest } from "../../../../services/soap/invoice/requests/CreateInvoicesFromItemsRequest";
import { DeleteProjectItemRequest } from "../../../../services/soap/item/requests/DeleteProjectItemRequest";
import styles from "./project_item_grid_component.module.css";
import { CreateProjectItemRequest } from "../../../../services/soap/item/requests/CreateProjectItemRequest";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { store } from "../../../../state/store"; // Keep for future use

interface ProjectItemGridProps {
  showpurchasePrice?: boolean;
  showPurchasePriceCCY?: boolean;
  showSalesPriceBCCY?: boolean;
  showCatalogPrice?: boolean;
  showInvoiceContact?: boolean;
  showControllingData?: boolean;
  showBillingDate?: boolean;
  showCustomAttributes?: boolean;
  showTrackingCode?: boolean;
  showSupplier?: boolean;
  componentTitle?: string;
  minHeight?: number;
  selectedItem?: FPAData;
}

interface ColumnDefinition {
  name: string;
  key: string;
  width?: number;
}

function ProjectItemGrid({
  showpurchasePrice,
  showPurchasePriceCCY,
  showSalesPriceBCCY,
  showCatalogPrice,
  showInvoiceContact,
  showControllingData,
  showBillingDate,
  showCustomAttributes,
  showTrackingCode,
  showSupplier,
  componentTitle = "Business Case",
  minHeight = 64,
  selectedItem,
}: ProjectItemGridProps) {
  const { t } = useTranslation();
  const componentRef = useRef<HTMLDivElement>(null);
  const [, setComponentWidth] = useState<number>(0);
  const [reloadComponent] = useComonentReload();
  const { itemService, store, projectService, financeService, invoiceService } =
    useDependency();
  const { isMobile } = useViewInfo();
  const [rowsData, setRowsData] = useState<any[]>([]);
  const [showLoader, setShowLoader] = useState<boolean>(true);

  // Add item mode state
  const [isAddingItem, setIsAddingItem] = useState<boolean>(false);
  const [newItemData, setNewItemData] = useState<any>(null);

  // Use reducer for all related project calculation state
  const [projectState, dispatch] = useReducer(
    projectCalculationReducer,
    initialProjectCalculationState,
  );

  // Use reducer for selection state
  const [selectionState, dispatchSelection] = useReducer(
    selectionReducer,
    initialSelectionState,
  );

  // Destructure for easier access
  const {
    calculation: projectItemCalculation,
    original: originalCalculation,
    discountPercentage: discountPercentageInput,
    discountValueBccy: discountValueBccyInput,
    discountValue: discountValueInput,
    changeFlags,
    currencyRate,
    tempRate,
    tempCurrency,
    selectedCurrency,
  } = projectState;

  const { selectedIds } = selectionState;

  const [showMarginGraphic, setShowMarginGraphic] = useState<boolean>(true);

  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(
    null,
  );
  const [selectedInvoiceToId, setSelectedInvoiceToId] = useState<string | null>(
    null,
  );
  const [providerInputValue, setProviderInputValue] = useState<string>("");
  const [invoiceToInputValue, setInvoiceToInputValue] = useState<string>("");

  // Provider async list
  const providerList = useAsyncList({
    async load({ signal, filterText }) {
      if (!filterText || filterText.length < 2) {
        return { items: [] };
      }

      try {
        const list = await projectService.listFPA2(
          new ListFPARequest2(store.Server, store.SessionId, {
            limit: 50,
            offset: 0,
            flatView: true,
            fulltext: filterText + "*",
            folderFilter: {
              COL: [
                {
                  name: "fpaTypeCategories",
                  type: "any",
                  value: "com.atollon.project.fpatypecategory.organization",
                },
              ],
            },
            ORDER: {
              COL: [
                {
                  name: "name",
                  desc: false,
                },
                {
                  name: "created",
                  desc: true,
                },
              ],
            },
          }),
        );

        if (!list || list.count === 0 || !list.ITEMS) {
          return { items: [] };
        }

        const items = list.ITEMS.map((item: any) => {
          const innerItem = item.ITEM.FOLDER;
          return {
            id: innerItem.id,
            name: `${innerItem.name} (${innerItem.folderTypeName})`,
          };
        });

        return { items };
      } catch (error) {
        console.error("Error fetching provider list:", error);
        return { items: [] };
      }
    },
  });

  // Invoice To async list
  const invoiceToList = useAsyncList({
    async load({ signal, filterText }) {
      if (!filterText || filterText.length < 2) {
        return { items: [] };
      }

      try {
        const list = await projectService.listFPA2(
          new ListFPARequest2(store.Server, store.SessionId, {
            limit: 50,
            offset: 0,
            flatView: true,
            fulltext: filterText + "*",
            folderFilter: {
              COL: [
                {
                  name: "fpaTypeCategories",
                  type: "any",
                  value: "com.atollon.project.fpatypecategory.client",
                },
              ],
            },
            ORDER: {
              COL: [
                {
                  name: "name",
                  desc: false,
                },
                {
                  name: "created",
                  desc: true,
                },
              ],
            },
          }),
        );

        if (!list || list.count === 0 || !list.ITEMS) {
          return { items: [] };
        }

        const items = list.ITEMS.map((item: any) => {
          const innerItem = item.ITEM.FOLDER;
          return {
            id: innerItem.id,
            name: `${innerItem.name} (${innerItem.folderTypeName})`,
          };
        });

        return { items };
      } catch (error) {
        console.error("Error fetching invoice list:", error);
        return { items: [] };
      }
    },
  });

  const [probabilityTerm, setProbabilityTerm] = useState<string>("");
  const debouncedProbabilityTerm = useDebounce(probabilityTerm, 1000);
  const [showExchangeRateDialog, setShowExchangeRateDialog] =
    useState<boolean>(false);
  const [pendingExpectedCloseDate, setPendingExpectedCloseDate] = useState<
    string | null
  >(null);
  const { currencies } = usePreloadAssets();

  // Currency rate dialog states
  const [showCurrencyRateDialog, setShowCurrencyRateDialog] =
    useState<boolean>(false);
  const projectRawData = useRef<any>(null);

  // Define base columns structure (memoized)
  const baseColumnsData = useMemo<any[]>(
    () => [
      {
        name: t("product_name", { ns: "layout_components" }),
        key: "productName",
      },
      {
        name: t("quantity", { ns: "layout_components" }),
        key: "quantity",
        width: 100,
      },
      {
        name: t("sold_quantity", { ns: "layout_components" }),
        key: "soldQuantity",
        width: 120,
      }, //Custom
      {
        name: t("remaining_sold", { ns: "layout_components" }),
        key: "remainingSold",
        width: 130,
      }, //Custom
      {
        name: t("purchase_quantity", { ns: "layout_components" }),
        key: "purchaseQuantity",
        width: 140,
      }, //Custom
      {
        name: t("remaining_purchase", { ns: "layout_components" }),
        key: "remainingPurchase",
        width: 160,
      }, //Custom
      {
        name: t("attrib0", { ns: "layout_components" }),
        key: "attrib0",
        width: 100,
      }, //Custom
      {
        name: t("unit_sales_price", { ns: "layout_components" }),
        key: "unitPriceCurr",
        width: 150,
      },
      {
        name: t("sales_price", { ns: "layout_components" }),
        key: "totalAmountBccy",
        width: 100,
      }, //Custom
      {
        name: t("total_sales_price", { ns: "layout_components" }),
        key: "totalPriceCurr",
        width: 140,
      },
      {
        name: t("unit_purchase_price", { ns: "layout_components" }),
        key: "purchasePriceCurr",
        width: 150,
      },
      {
        name: t("purchase_price", { ns: "layout_components" }),
        key: "totalPurchaseBccy",
        width: 150,
      }, //Custom
      {
        name: t("billing_date", { ns: "layout_components" }),
        key: "billingDateFormatted",
        width: 100,
      }, //Custom
      {
        name: t("supplier", { ns: "layout_components" }),
        key: "supplierName",
        width: 150,
      }, //Custom
      {
        name: t("ratio", { ns: "layout_components" }),
        key: "ratio",
        width: 50,
      },
    ],
    [t],
  );

  // Define base mobile columns structure (memoized)
  const baseColumnsDataMobile = useMemo<any[]>(
    () => [
      {
        name: t("product_name", { ns: "layout_components" }),
        key: "productName",
      },
      {
        name: t("quantity", { ns: "layout_components" }),
        key: "quantity",
        width: 100,
      },
      {
        name: t("sold_quantity", { ns: "layout_components" }),
        key: "soldQuantity",
        width: 120,
      }, //Custom
      {
        name: t("remaining_sold", { ns: "layout_components" }),
        key: "remainingSold",
        width: 130,
      }, //Custom
      {
        name: t("purchase_quantity", { ns: "layout_components" }),
        key: "purchaseQuantity",
        width: 140,
      }, //Custom
      {
        name: t("remaining_purchase", { ns: "layout_components" }),
        key: "remainingPurchase",
        width: 160,
      }, //Custom
      {
        name: t("attrib0", { ns: "layout_components" }),
        key: "attrib0",
        width: 100,
      }, //Custom
      {
        name: t("unit_sales_price", { ns: "layout_components" }),
        key: "unitPriceCurr",
        width: 150,
      },
      {
        name: t("sales_price", { ns: "layout_components" }),
        key: "totalAmountBccy",
        width: 100,
      }, //Custom
      {
        name: t("total_sales_price", { ns: "layout_components" }),
        key: "totalPriceCurr",
        width: 140,
      },
      {
        name: t("unit_purchase_price", { ns: "layout_components" }),
        key: "purchasePriceCurr",
        width: 150,
      },
      {
        name: t("purchase_price", { ns: "layout_components" }),
        key: "totalPurchaseBccy",
        width: 150,
      }, //Custom
      {
        name: t("billing_date", { ns: "layout_components" }),
        key: "billingDateFormatted",
        width: 100,
      }, //Custom
      {
        name: t("supplier", { ns: "layout_components" }),
        key: "supplierName",
        width: 150,
      }, //Custom
      {
        name: t("ratio", { ns: "layout_components" }),
        key: "ratio",
        width: 100,
      },
    ],
    [t],
  );

  // Memoize column visibility configuration
  const columnsToHide = useMemo(
    () => [
      { key: "totalPurchaseBccy", visible: showpurchasePrice },
      { key: "totalAmountBccy", visible: showSalesPriceBCCY },
      { key: "attrib0", visible: showCustomAttributes },
      { key: "billingDateFormatted", visible: showBillingDate },
      { key: "supplierName", visible: showSupplier },
      { key: "soldQuantity", visible: showControllingData },
      { key: "remainingSold", visible: showControllingData },
      { key: "purchaseQuantity", visible: showControllingData },
      { key: "remainingPurchase", visible: showControllingData },
    ],
    [
      showpurchasePrice,
      showSalesPriceBCCY,
      showCustomAttributes,
      showBillingDate,
      showSupplier,
      showControllingData,
    ],
  );

  // Memoize filter function
  const filterColumns = useCallback(
    (columns: any[]) =>
      columns.filter((column) =>
        columnsToHide.every((col) => col.visible || col.key !== column.key),
      ),
    [columnsToHide],
  );

  // Apply filtering with memoization
  const columnsData = useMemo(
    () => filterColumns(baseColumnsData),
    [filterColumns, baseColumnsData],
  );

  const columnsDataMobile = useMemo(
    () => filterColumns(baseColumnsDataMobile),
    [filterColumns, baseColumnsDataMobile],
  );

  useEffect(() => {
    if (!componentRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setComponentWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(componentRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const loadProjectItemCalculation = useCallback(
    async (projectId: string) => {
      try {
        const response: any = await itemService.listProjectItemCalculation(
          new ListProjectItemCalculationRequest(
            store.Server,
            store.SessionId,
            undefined,
            projectId,
          ),
        );

        if (response?.result === "OK" && response?.ITEM) {
          //console.log("response.ITEM", response.ITEM);
          // Single dispatch to update all related state atomically
          dispatch({ type: "LOAD_PROJECT_ITEM", payload: response.ITEM });
        }
      } catch (error) {
        console.error("Error loading project item calculation:", error);
        ToastQueue.negative("Failed to load project item calculation", {
          timeout: 3000,
        });
      }
    },
    [itemService, store.Server, store.SessionId],
  );

  useEffect(() => {
    (async () => {
      if (!selectedItem?.id) {
        setShowLoader(false);
        return;
      }

      setShowLoader(true);
      try {
        await Promise.all([
          loadProjectItemGridData(),
          loadProjectItemCalculation(String(selectedItem.id)),
          loadProjectDetails(selectedItem.id as number),
        ]);
      } catch (error) {
        console.error("Error loading project data:", error);
      } finally {
        setShowLoader(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem, reloadComponent, loadProjectItemCalculation]);

  useEffect(() => {
    if (!debouncedProbabilityTerm) return;
    // Only update if the debounced term is different from the original server value
    const originalProbability =
      originalCalculation?.probability?.toString().replace("%", "").trim() ||
      "";
    if (debouncedProbabilityTerm === originalProbability) return;

    // console.log('Probability update triggered by user input:', {
    //   debouncedProbabilityTerm,
    //   originalProbability
    // });

    updateProbabilityValue(debouncedProbabilityTerm).catch((error) => {
      console.error("Error updating probability value:", error);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedProbabilityTerm]);

  const updateProjectItemGridData = async (projectItemGridData: any[]) => {
    if (!projectItemGridData) return [];
    return Promise.all(
      projectItemGridData.map(async (item) => {
        const partNumber = await getPartNumber(item);
        return {
          ...item,
          _refId: `${partNumber}_${item.productName}_ref`,
          partNumber,
        };
      }),
    );
  };

  const setRecordIds = (record: any, selectedItem: any) => {
    switch (selectedItem?.type) {
      case FPADataTypes.ACTIVITY:
        record.folderId = selectedItem?.parent?.parent_id;
        record.projectId = String(selectedItem?.parent_id);
        record.activityId = String(selectedItem?.id);
        break;
      case FPADataTypes.PROJECT:
        record.folderId = "";
        record.projectId = String(selectedItem?.id);
        record.activityId = "";
        break;
      case FPADataTypes.FOLDER:
        record.folderId = String(selectedItem?.id);
        record.projectId = "";
        record.activityId = "";
        break;
    }
  };

  // Function to load project product data
  const loadProjectItemGridData = useCallback(async () => {
    const record: any = {};
    setRecordIds(record, selectedItem);

    // Helper to format price with currency
    const formatPrice = (value: number | string, currency: string): string => {
      const numValue = typeof value === "string" ? parseFloat(value) : value;
      return `${formatDecimalToString(numValue.toFixed(2))} ${currency}`;
    };

    // Call API to fetch project product data
    const projecrProdRes: any = await itemService.listProjectItem(
      new ListProjectItemRequest(
        store.Server,
        store.SessionId,
        50,
        0,
        new ListProjectItemRequestOptions_ItemGrid(record.projectId),
      ),
    );

    // If API call is successful
    if (projecrProdRes?.result === "OK") {
      // Map the project product data to desired format
      projectRawData.current = projecrProdRes;

      if (!projecrProdRes?.ROWS || projecrProdRes.ROWS.length === 0) {
        console.log("No project items found");
        setRowsData([]);
        return;
      }
      console.log("projecrProdRes.ROWS", projecrProdRes.ROWS);

      // First pass: create flat items with parent reference
      const flatItems = projecrProdRes?.ROWS?.map(
        (item: any, index: number) => {
          const { ITEM } = item;
          const { currency } = ITEM;

          return {
            id: ITEM.id,
            // Store parent ID for tree structure - similar to ProductSelectionExample
            parentId: ITEM.parentId || null,
            sortOrder: index,
            quantity: formatDecimalToString(
              parseFloat(ITEM.quantity).toFixed(2),
            ),
            partID: ITEM.item,
            _refId: "",
            partNumber: "Part Number",
            productName: ITEM.name,
            supplier: ITEM.supplier,
            supplierName: ITEM.supplierName,
            currency,
            vatValue: ITEM.vatValue,
            amountWithVat: ITEM.amountWithVat,
            unitPrice: formatDecimalToString(ITEM.amount),
            unitPriceCurr: formatPrice(ITEM.amount, currency),
            totalPrice: formatDecimalToString(
              parseFloat(ITEM.totalAmount).toFixed(2),
            ),
            totalPriceCurr: formatPrice(ITEM.totalAmount, currency),
            purchasePrice: ITEM.totalPurchase,
            purchasePriceCurr: formatPrice(ITEM.totalPurchase, currency),
            totalPurchase: ITEM.totalPurchase,
            totalPurchaseCurr: formatPrice(ITEM.totalPurchase, currency),
            totalPurchaseBccy: ITEM.totalPurchaseBccy,
            totalPurchaseBccyCurr: formatPrice(
              ITEM.totalPurchaseBccy,
              currency,
            ),
            totalAmountBccy: ITEM.totalAmountBccy,
            totalAmountBccyCurr: formatPrice(ITEM.totalAmountBccy, currency),
            totalAmountWithVat: ITEM.totalAmountWithVat,
            refId: ITEM.refId,
            billingDate: ITEM.billingDate,
            billingDateFormatted: moment(ITEM.billingDate).format("MM/DD/YYYY"),
            ratio: (ITEM.ratio || "0") + "%",
            attrib0: ITEM.attrib0,
            soldQuantity: formatDecimalToString(
              parseFloat(ITEM.totalQuantity).toFixed(2),
            ),
            remainingSold: "0",
            purchaseQuantity: formatDecimalToString(
              parseFloat(ITEM.freeValue).toFixed(2),
            ),
            remainingPurchase: "0",
            currencyRate: ITEM.currencyRate,
          };
        },
      );

      // Build tree structure similar to ProductSelectionExample
      // Create a map for quick lookup
      const itemMap = new Map<string, any>();
      flatItems.forEach((item: any) => {
        item.childrenIds = [];
        itemMap.set(item.id, item);
      });

      // Calculate childrenIds for each parent
      flatItems.forEach((item: any) => {
        if (item.parentId && itemMap.has(item.parentId)) {
          const parent = itemMap.get(item.parentId);
          parent.childrenIds.push(item.id);
        }
      });

      // Calculate depth for each item (recursive)
      const calculateDepth = (item: any): number => {
        if (!item.parentId) return 0;
        const parent = itemMap.get(item.parentId);
        if (!parent) return 0;
        return 1 + calculateDepth(parent);
      };

      // Build final tree data with proper TreeGridRow structure
      const projectItemGridData = flatItems.map((item: any) => ({
        ...item,
        parentId: item.parentId || null,
        depth: calculateDepth(item),
        childrenIds: item.childrenIds || [],
        isExpanded: item.childrenIds.length > 0, // Expand parents by default
      }));

      const rate = projectItemGridData[0]?.currencyRate || "1";
      dispatch({ type: "UPDATE_CURRENCY_RATE", rate });
      dispatch({ type: "UPDATE_TEMP_RATE", rate });

      // Update project product data with additional information
      updateProjectItemGridData(projectItemGridData).then(
        (updatedData: any) => {
          setRowsData(updatedData);
        },
      );
    } else {
      console.error("API call failed:", projecrProdRes);
      setRowsData([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem, itemService, store.Server, store.SessionId]);

  const getPartNumber = async (item: any) => {
    const itemResp = await itemService.getItem(
      new GetItemRequest(store.Server, store.SessionId, item.partID),
    );
    return itemResp.ITEM.refId;
  };

  // Search products function for ComboBox
  const searchProducts = useCallback(
    async (filterText: string): Promise<ComboBoxItem[]> => {
      if (filterText.length < 2) {
        return [];
      }

      try {
        // Search by name and refId concurrently
        const [itemResp, itemRespByRefID]: any = await Promise.all([
          itemService.listItem(
            new ListItemRequest(store.Server, store.SessionId, 15, 0, {
              searchName: filterText.trim(),
              statusShow: "1",
            }),
          ),
          itemService.listItem(
            new ListItemRequest(store.Server, store.SessionId, 15, 0, {
              refId: filterText.trim(),
              statusShow: "1",
            }),
          ),
        ]);

        const results: ComboBoxItem[] = [];

        // Format items from refId search
        if (itemRespByRefID.ITEM?.length) {
          itemRespByRefID.ITEM.forEach((item: any) => {
            results.push({
              ...item,
              id: `${item.id}_ref`,
              label: `${item.refId} (${item.name})`,
              sku: item.refId,
              unitPrice: parseFloat(item.amount) || 0,
              catalogPrice: parseFloat(item.amountWithVat) || 0,
              category: "SKU Match",
              productId: item.id,
              productName: item.name,
              amountWithVat: parseFloat(item.amountWithVat) || 0,
              vatValue: parseFloat(item.vatValue) || 0,
              purchasePrice: parseFloat(item.purchasePrice) || 0,
              currency: item.currencyName || "CZK",
            });
          });
        }

        // Format items from name search
        if (itemResp.ITEM?.length) {
          itemResp.ITEM.forEach((item: any) => {
            // Avoid duplicates
            if (!results.some((r) => r.productId === item.id)) {
              results.push({
                ...item,
                id: `${item.id}_name`,
                label: item.name + (item.refId ? ` (${item.refId})` : ""),
                sku: item.refId || "",
                unitPrice: parseFloat(item.amount) || 0,
                catalogPrice: parseFloat(item.amountWithVat) || 0,
                category: "Name Match",
                productId: item.id,
                productName: item.name,
                amountWithVat: parseFloat(item.amountWithVat) || 0,
                vatValue: parseFloat(item.vatValue) || 0,
                purchasePrice: parseFloat(item.purchasePrice) || 0,
                currency: item.currencyName || "CZK",
              });
            }
          });
        }

        return results;
      } catch (error) {
        console.error("Product search error:", error);
        return [];
      }
    },
    [itemService, store.Server, store.SessionId],
  );

  const getCurrencyRate = useCallback(
    async (currency_id: string, rateDate: string): Promise<number> => {
      try {
        const currency_rate: any = await financeService.listCurrencyRate(
          new ListCurrencyRateRequest(store.Server, store.SessionId, {
            currency: currency_id,
            rateDate: rateDate,
          }),
        );

        if (
          !currency_rate ||
          !currency_rate.currencyRate ||
          !currency_rate.currencyRate.rate
        ) {
          console.error("Invalid currency rate response:", currency_rate);
          ToastQueue.negative("Invalid currency rate response", {
            timeout: 3000,
          });
          return 1;
        }

        const rate = currency_rate.currencyRate.rate;
        dispatch({ type: "UPDATE_CURRENCY_RATE", rate });
        return rate;
      } catch (error) {
        console.error("Error fetching currency rate:", error);
        ToastQueue.negative("Failed to fetch currency rate", { timeout: 3000 });
        return 1;
      }
    },
    [financeService, store.Server, store.SessionId],
  );

  const getTempCurrencyRate = useCallback(
    async (currency_id: string, rateDate: string): Promise<number> => {
      try {
        const currency_rate: any = await financeService.listCurrencyRate(
          new ListCurrencyRateRequest(store.Server, store.SessionId, {
            currency: currency_id,
            rateDate: rateDate,
          }),
        );

        if (
          !currency_rate ||
          !currency_rate.currencyRate ||
          !currency_rate.currencyRate.rate
        ) {
          console.error("Invalid currency rate response:", currency_rate);
          ToastQueue.negative("Invalid currency rate response", {
            timeout: 3000,
          });
          return 1;
        }
        //console.log(" currency rate response:", currency_rate);
        const rate = currency_rate.currencyRate.rate;
        dispatch({ type: "UPDATE_TEMP_RATE", rate });
        return rate;
      } catch (error) {
        console.error("Error fetching temporary currency rate:", error);
        ToastQueue.negative("Failed to fetch currency rate", { timeout: 3000 });
        return 1;
      }
    },
    [financeService, store.Server, store.SessionId],
  );

  // Calculate Gross Profit and Margin dynamically
  // Gross Profit CZK = Deal Value CZK - Purchase Cost CZK (same as Margin CZK)
  const grossProfitCZK = useMemo(() => {
    // Use amountWithoutVatBccy from API if available (for initial load precision)
    // Otherwise calculate from amountWithoutVat * rate (for dynamic updates)
    const apiDealValueCZK =
      parseFloat(projectItemCalculation.amountWithoutVatBccy || "0") || 0;
    const dealValue = parseFloat(projectItemCalculation.amountWithoutVat) || 0;
    const rate = currencyRate ? parseFloat(currencyRate) : 1;
    const calculatedDealValueCZK = Math.round(dealValue * rate * 100) / 100;

    // Use API value if it exists and is close to calculated (no user edits), otherwise use calculated
    const dealValueCZK =
      apiDealValueCZK > 0 &&
      !changeFlags.isDiscountChanged &&
      !changeFlags.isDiscountValueChanged &&
      !changeFlags.isDealValueChanged
        ? apiDealValueCZK
        : calculatedDealValueCZK;

    const purchaseCostCZK =
      parseFloat(projectItemCalculation.purchasePriceBccy || "0") || 0;
    return Math.round((dealValueCZK - purchaseCostCZK) * 100) / 100;
  }, [
    projectItemCalculation.amountWithoutVat,
    projectItemCalculation.amountWithoutVatBccy,
    projectItemCalculation.purchasePriceBccy,
    currencyRate,
    changeFlags.isDiscountChanged,
    changeFlags.isDiscountValueChanged,
    changeFlags.isDealValueChanged,
  ]);

  const grossProfit = useMemo(() => {
    const dealValue = parseFloat(projectItemCalculation.amountWithoutVat) || 0;
    const purchaseCost =
      parseFloat(projectItemCalculation.totalPurchase || "0") || 0;
    return dealValue - purchaseCost;
  }, [projectItemCalculation]);

  const marginPercentage = useMemo(() => {
    // Use amountWithoutVatBccy from API if available (for initial load precision)
    const apiDealValueCZK =
      parseFloat(projectItemCalculation.amountWithoutVatBccy || "0") || 0;
    const dealValue = parseFloat(projectItemCalculation.amountWithoutVat) || 0;
    const rate = currencyRate ? parseFloat(currencyRate) : 1;
    const calculatedDealValueCZK = Math.round(dealValue * rate * 100) / 100;

    // Use API value if it exists and no user edits, otherwise use calculated
    const dealValueCZK =
      apiDealValueCZK > 0 &&
      !changeFlags.isDiscountChanged &&
      !changeFlags.isDiscountValueChanged &&
      !changeFlags.isDealValueChanged
        ? apiDealValueCZK
        : calculatedDealValueCZK;

    if (dealValueCZK === 0) return 0;
    // Margin % = (Gross Profit CZK / Deal Value CZK) * 100
    return (grossProfitCZK / dealValueCZK) * 100;
  }, [
    grossProfitCZK,
    projectItemCalculation.amountWithoutVat,
    projectItemCalculation.amountWithoutVatBccy,
    currencyRate,
    changeFlags.isDiscountChanged,
    changeFlags.isDiscountValueChanged,
    changeFlags.isDealValueChanged,
  ]);

  const marginValueCZK = useMemo(() => {
    return grossProfitCZK;
  }, [grossProfitCZK]);

  const marginValue = useMemo(() => {
    return grossProfit;
  }, [grossProfit]);

  // Format number with thousands separator
  const formatCurrency = useCallback((value: number): string => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }, []);

  // Calculate bar width percentage (capped at 100%)
  const barWidthPercentage = useMemo(() => {
    const percentage = Math.abs(marginPercentage);
    return Math.min(percentage, 100);
  }, [marginPercentage]);

  // Discount calculations now handled in reducer

  const handleSaveDealValue = useCallback(async () => {
    if (!selectedItem?.id) {
      ToastQueue.negative("No project selected", { timeout: 3000 });
      return;
    }

    try {
      const res = await itemService.modifyProjectItemValues(
        new ModifyProjectItemValuesRequest(store.Server, store.SessionId, {
          projectId: selectedItem?.id?.toString() || "",
          billingStatus: 0,
          method: "SetSalesBySales",
          value: projectItemCalculation.amountWithoutVat,
        }),
      );

      if (res?.result === "OK") {
        ToastQueue.positive("Deal value updated successfully", {
          timeout: 3000,
        });
        await loadProjectItemCalculation(String(selectedItem?.id));
        dispatch({ type: "SAVE_DEAL_VALUE" });
      } else {
        ToastQueue.negative("Failed to update deal value", { timeout: 3000 });
      }
    } catch (error) {
      console.error("Error saving deal value:", error);
      ToastQueue.negative("Error updating deal value", { timeout: 3000 });
    }
  }, [
    selectedItem?.id,
    itemService,
    store.Server,
    store.SessionId,
    projectItemCalculation,
    loadProjectItemCalculation,
  ]);

  const handleCancelDealValue = useCallback(() => {
    dispatch({ type: "CANCEL_DEAL_VALUE" });
  }, []);

  const handleSavePurchaseCost = useCallback(async () => {
    if (!selectedItem?.id) {
      ToastQueue.negative("No project item selected", { timeout: 3000 });
      return;
    }

    try {
      const res = await itemService.modifyProjectItemValues(
        new ModifyProjectItemValuesRequest(store.Server, store.SessionId, {
          projectId: selectedItem?.id?.toString() || "",
          billingStatus: 0,
          method: "SetPurchaseByPurchase",
          value: projectItemCalculation.totalPurchase,
        }),
      );

      if (res?.result === "OK") {
        ToastQueue.positive("Purchase cost updated successfully", {
          timeout: 3000,
        });
        await Promise.all([
          loadProjectItemCalculation(String(selectedItem?.id)),
          loadProjectItemGridData(),
        ]);
        dispatch({ type: "SAVE_PURCHASE_COST" });
      } else {
        ToastQueue.negative("Failed to update purchase cost", {
          timeout: 3000,
        });
      }
    } catch (error) {
      console.error("Error saving purchase cost:", error);
      ToastQueue.negative("Error updating purchase cost", { timeout: 3000 });
    }
  }, [
    selectedItem?.id,
    itemService,
    store.Server,
    store.SessionId,
    projectItemCalculation.totalPurchase,
    loadProjectItemCalculation,
    loadProjectItemGridData,
  ]);

  const handleCancelPurchaseCost = useCallback(() => {
    dispatch({ type: "CANCEL_PURCHASE_COST" });
  }, []);

  const handleSaveCatalogPrice = useCallback(async () => {
    try {
      const res = await itemService.modifyProjectItemValues(
        new ModifyProjectItemValuesRequest(store.Server, store.SessionId, {
          projectId: selectedItem?.id?.toString() || "",
          billingStatus: "0",
          method: "SetSalesByDiscount",
          value: discountValueInput,
        }),
      );

      if (res?.result === "OK") {
        ToastQueue.positive("Discount value updated successfully", {
          timeout: 3000,
        });
        await Promise.all([
          loadProjectItemCalculation(String(selectedItem?.id)),
          loadProjectItemGridData(),
        ]);
        dispatch({ type: "SAVE_CATALOG_PRICE" });
      } else {
        ToastQueue.negative("Failed to update discount value", {
          timeout: 3000,
        });
      }
    } catch (error) {
      console.error("Error saving catalog price:", error);
      ToastQueue.negative("Error updating discount value", { timeout: 3000 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedItem?.id,
    itemService,
    store.Server,
    store.SessionId,
    discountValueInput,
    loadProjectItemCalculation,
    loadProjectItemGridData,
  ]);

  const handleCancelCatalogPrice = useCallback(() => {
    dispatch({ type: "CANCEL_CATALOG_PRICE" });
  }, []);

  const handleSaveMargin = useCallback(async () => {
    if (!selectedItem?.id) {
      ToastQueue.negative("No project selected", { timeout: 3000 });
      return;
    }

    try {
      const res = await itemService.modifyProjectItemValues(
        new ModifyProjectItemValuesRequest(store.Server, store.SessionId, {
          projectId: selectedItem?.id?.toString() || "",
          billingStatus: 0,
          method: "SetSalesByPurchase",
          value: projectItemCalculation.amountWithoutVat,
        }),
      );

      if (res?.result === "OK") {
        ToastQueue.positive("Margin updated successfully", { timeout: 3000 });
        await loadProjectItemCalculation(String(selectedItem?.id));
        dispatch({ type: "SAVE_MARGIN" });
      } else {
        ToastQueue.negative("Failed to update margin", { timeout: 3000 });
      }
    } catch (error) {
      console.error("Error saving margin:", error);
      ToastQueue.negative("Error updating margin", { timeout: 3000 });
    }
  }, [
    selectedItem?.id,
    itemService,
    store.Server,
    store.SessionId,
    projectItemCalculation,
    loadProjectItemCalculation,
  ]);

  const handleCancelMargin = useCallback(() => {
    dispatch({ type: "CANCEL_MARGIN" });
  }, []);

  const handleDeleteRow = useCallback(
    async (rowId: string) => {
      if (!selectedItem?.id) {
        ToastQueue.negative("No project selected", { timeout: 3000 });
        return;
      }

      try {
        setShowLoader(true);
        //console.log('record', record);

        let result = await itemService.deleteProjectItem(
          new DeleteProjectItemRequest(store.Server, store.SessionId, rowId),
        );

        // console.log('result', result);
        if (result.result === "OK") {
          loadProjectItemCalculation(String(selectedItem.id));
          loadProjectDetails(selectedItem.id as number);
          loadProjectItemGridData();

          ToastQueue.positive("Record deleted successfully", { timeout: 3000 });
        } else {
          ToastQueue.negative(result.EXCEPTION.reason, { timeout: 3000 });
        }
      } catch (error) {
      } finally {
        setShowLoader(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      selectedItem?.id,
      itemService,
      store.Server,
      store.SessionId,
      loadProjectItemCalculation,
    ],
  );

  // Handle Add Item button click - creates a new row in edit mode
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleStartAddItem = useCallback(() => {
    const newRow = {
      id: `new-${Date.now()}`,
      isNew: true,
      productName: "",
      description: "",
      quantity: 1,
      unitPrice: 0,
      totalPrice: 0,
      currency: projectItemCalculation.currency || "CZK",
      vatValue: 0,
      amountWithVat: 0,
      purchasePrice: 0,
      partID: "",
      refId: "",
    };
    setNewItemData(newRow);
    setIsAddingItem(true);
  }, [projectItemCalculation.currency]);

  // Handle cancel add item
  const handleCancelAddItem = useCallback(() => {
    setIsAddingItem(false);
    setNewItemData(null);
  }, []);

  // Handle save new item
  const handleSaveNewItem = useCallback(async () => {
    if (!selectedItem?.id) {
      ToastQueue.negative("No project selected", { timeout: 3000 });
      return;
    }

    if (!newItemData?.productName) {
      ToastQueue.negative("Please select a product", { timeout: 3000 });
      return;
    }

    setShowLoader(true);
    try {
      const record: any = {};
      setRecordIds(record, selectedItem);

      // Use description if modified, otherwise use product name
      //console.log("newItemData.description", newItemData.description);
      //console.log("newItemData.productName", newItemData.productName);
      const itemName = newItemData.description || newItemData.productName;

      const result = await itemService.createProjectItem(
        new CreateProjectItemRequest(
          store.Server,
          store.SessionId,
          generateProjectProductItem(
            record.folderId || "",
            record.projectId || "",
            record.activityId || "",
            newItemData.partID || "",
            itemName,
            newItemData.quantity || 1,
            newItemData.unitPrice || 0,
            newItemData.totalPrice || 0,
            newItemData.amountWithVat || 0,
            newItemData.currency || "",
            String(newItemData.vatValue || 0),
            String(newItemData.purchasePrice || 0),
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            newItemData.refId || "",
            new Date().toISOString().split("T")[0],
            "0",
            { type: "0", ratio: record.ratio || "0" },
          ),
        ),
      );

      if (result.result === "OK") {
        ToastQueue.positive("Item added successfully", { timeout: 3000 });
        setIsAddingItem(false);
        setNewItemData(null);
        // Refresh data
        await Promise.all([
          loadProjectItemCalculation(String(selectedItem.id)),
          loadProjectItemGridData(),
        ]);
      } else {
        ToastQueue.negative(result.EXCEPTION?.reason || "Failed to add item", {
          timeout: 3000,
        });
      }
    } catch (error) {
      console.error("Error adding item:", error);
      ToastQueue.negative("Error adding item", { timeout: 3000 });
    } finally {
      setShowLoader(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedItem,
    newItemData,
    itemService,
    store.Server,
    store.SessionId,
    loadProjectItemCalculation,
    loadProjectItemGridData,
  ]);

  // Handle row update for the new item being added
  const handleNewItemRowUpdate = useCallback((updatedRow: TreeGridRow) => {
    const data = updatedRow.data;
    const quantity = parseFloat(data.quantity) || 1;
    const unitPrice = parseFloat(data.unitPrice) || 0;
    const totalPrice = quantity * unitPrice;
    const vatValue = parseFloat(data.vatValue) || 0;
    const amountWithVat = totalPrice * (1 + vatValue / 100);
    console.log("data", data);
    setNewItemData((prev: any) => ({
      ...prev,
      ...data,
      quantity,
      unitPrice,
      totalPrice,
      amountWithVat,
      description:
        data.description !== undefined ? data.description : data.productName,
    }));
  }, []);

  // Generic handler for creating invoice documents
  const createInvoiceDocument = useCallback(
    async (documentType: string, documentName: string) => {
      if (!selectedItem) {
        ToastQueue.negative("No project selected", { timeout: 3000 });
        return;
      }

      setShowLoader(true);
      try {
        const response = await invoiceService.createInvoicesFromItems(
          new CreateInvoicesFromItemsRequest(store.Server, store.SessionId, {
            PROJECTS: [{ id: selectedItem.id.toString() }],
            /*ITEMS: rowsData,*/
            DOCUMENTTYPE: documentType,
          }),
        );

        if (response?.result === "OK") {
          const count = response.count || 0;
          const invoices: any[] = []; //response?.INVOICES || [];

          if (count > 0 && invoices.length > 0) {
            // Show toast with action button for each created invoice
            invoices.forEach((invoice: any, index: number) => {
              const invoiceId = invoice.id || invoice.ID || invoice.invoiceId;
              ToastQueue.positive(
                `${documentName} ${index + 1} created successfully`,
                {
                  timeout: 5000,
                  actionLabel: "Open",
                  onAction: () => {
                    window.location.href = `atollon://finance/openInvoice/?id=${invoiceId}`;
                  },
                },
              );
            });
          } else {
            ToastQueue.negative("No document created", { timeout: 3000 });
          }
        } else {
          ToastQueue.negative(
            `Failed to create ${documentName.toLowerCase()}`,
            { timeout: 3000 },
          );
        }
      } catch (error) {
        console.error(`Error creating ${documentName.toLowerCase()}:`, error);
        ToastQueue.negative(`Error creating ${documentName.toLowerCase()}`, {
          timeout: 3000,
        });
      } finally {
        //clear selected  SET_SELECTED_IDS
        dispatchSelection({ type: "SET_SELECTED_IDS", selectedIds: [] });

        setShowLoader(false);
      }
    },
    [selectedItem, invoiceService, store],
  );

  const handleCreateEstimate = useCallback(() => {
    createInvoiceDocument("offer-issued", "Estimate");
  }, [createInvoiceDocument]);

  const handleCreateSalesOrder = useCallback(() => {
    createInvoiceDocument("order-received", "Sales order");
  }, [createInvoiceDocument]);

  const handleCreateInvoice = useCallback(() => {
    createInvoiceDocument("invoice-issued", "Invoice");
  }, [createInvoiceDocument]);

  const getProject = useCallback(
    async (projectId: number): Promise<any> => {
      try {
        const projectResponse = await projectService.getProject(
          new GetProjectRequest(store.Server, store.SessionId, projectId),
        );

        if (projectResponse.EXCEPTION) {
          console.error(
            "projectResponse.EXCEPTION " + projectResponse.EXCEPTION,
          );
          return null;
        }

        return projectResponse.PROJECT;
      } catch (error) {
        console.error("Error fetching project:", error);
        return null;
      }
    },
    [projectService, store.Server, store.SessionId],
  );

  const loadProjectDetails = useCallback(
    async (projectId: number) => {
      try {
        const project = await getProject(projectId);
        if (project) {
          // Set provider if exists
          if (project.provider) {
            setSelectedProviderId(project.provider);
            setProviderInputValue(project.providerName || "");
            providerList.setFilterText(project.providerName || "");
          }
          // Set invoice to (customer) if exists
          if (project.customer) {
            setSelectedInvoiceToId(project.customer);
            setInvoiceToInputValue(project.customerName || "");
            invoiceToList.setFilterText(project.customerName || "");
          }
          // Update expected close date if exists
          if (project.expectedCloseDate) {
            const formattedDate = moment(project.expectedCloseDate).format(
              "YYYY-MM-DD",
            );
            dispatch({ type: "UPDATE_FINAL_DATE", date: formattedDate });
            setPendingExpectedCloseDate(formattedDate);
          }
        }
      } catch (error) {
        console.error("Error loading project details:", error);
      }
    },
    [getProject, providerList, invoiceToList],
  );

  const updateProjectProvider = useCallback(
    async (providerId: any): Promise<boolean> => {
      if (!selectedItem?.id) {
        console.error("No selected item ID available");
        ToastQueue.negative("No project selected", { timeout: 3000 });
        return false;
      }

      try {
        const project = await getProject(selectedItem.id as number);
        if (!project) {
          ToastQueue.negative("Failed to load project details", {
            timeout: 3000,
          });
          return false;
        }

        const update = await projectService.updateProject(
          new UpdateProjectRequest(store.Server, store.SessionId, {
            ...project,
            provider: providerId,
          }),
        );

        if (update.EXCEPTION) {
          console.error("update.EXCEPTION " + update.EXCEPTION);
          ToastQueue.negative("Failed to update provider", { timeout: 3000 });
          return false;
        }

        ToastQueue.positive("Provider updated successfully", { timeout: 2000 });
        return true;
      } catch (error) {
        console.error("Error updating provider:", error);
        ToastQueue.negative("Error updating provider", { timeout: 3000 });
        return false;
      }
    },
    [
      selectedItem?.id,
      getProject,
      projectService,
      store.Server,
      store.SessionId,
    ],
  );

  const updateProjectInvoiceTo = useCallback(
    async (invoiceToId: any): Promise<boolean> => {
      if (!selectedItem?.id) {
        console.error("No selected item ID available");
        ToastQueue.negative("No project selected", { timeout: 3000 });
        return false;
      }

      try {
        const project = await getProject(selectedItem.id as number);
        if (!project) {
          ToastQueue.negative("Failed to load project details", {
            timeout: 3000,
          });
          return false;
        }

        const update = await projectService.updateProject(
          new UpdateProjectRequest(store.Server, store.SessionId, {
            ...project,
            customer: invoiceToId,
          }),
        );

        if (update.EXCEPTION) {
          console.error("update.EXCEPTION " + update.EXCEPTION);
          ToastQueue.negative("Failed to update invoice recipient", {
            timeout: 3000,
          });
          return false;
        }

        ToastQueue.positive("Invoice recipient updated successfully", {
          timeout: 2000,
        });
        return true;
      } catch (error) {
        console.error("Error updating invoice recipient:", error);
        ToastQueue.negative("Error updating invoice recipient", {
          timeout: 3000,
        });
        return false;
      }
    },
    [
      selectedItem?.id,
      getProject,
      projectService,
      store.Server,
      store.SessionId,
    ],
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const updateProjectExpectedCloseDate = useCallback(
    async (newDate: string, updateExchangeRate: boolean): Promise<boolean> => {
      try {
        if (updateExchangeRate && projectItemCalculation.currency !== "CZK") {
          const currency_id = currencies.find(
            (cur: any) => cur.code === projectItemCalculation.currency,
          )?.id;

          if (currency_id) {
            await getCurrencyRate(currency_id, newDate + "T00:00:00");
          }
        }

        dispatch({ type: "UPDATE_FINAL_DATE", date: newDate });
        return true;
      } catch (error) {
        console.error("Error updating expected close date:", error);
        ToastQueue.negative("Failed to update expected close date", {
          timeout: 3000,
        });
        return false;
      }
    },
    [projectItemCalculation, currencies, getCurrencyRate],
  );

  const updateExchangeRate = useCallback(
    async (updateFinal: boolean, overrideRate?: string): Promise<boolean> => {
      if (!selectedItem?.id) {
        ToastQueue.negative("No project selected", { timeout: 3000 });
        return false;
      }

      const rateToUse = overrideRate ?? tempRate;
      //console.log('Using rate:', rateToUse, 'Override:', overrideRate, 'tempRate:', tempRate);

      try {
        setShowLoader(true);
        // Update project item calculation
        const resProjectItem = await itemService.updateProjectItemCalculation(
          new UpdateProjectItemCalculationRequest(
            store.Server,
            store.SessionId,
            projectItemCalculation?.id?.toString() || "",
            [
              {
                ...projectItemCalculation,
                ...(updateFinal && { finalDate: pendingExpectedCloseDate }),
                projectId: selectedItem.id.toString(),
                currency: tempCurrency,
                modified: new Date(),
              } as any,
            ],
          ),
        );

        // Check if main update was successful
        if (resProjectItem?.result !== "OK") {
          ToastQueue.negative("Failed to update exchange rate", {
            timeout: 3000,
          });
          return false;
        }

        // Validate project raw data exists
        if (!projectRawData.current || !projectRawData.current.ROWS) {
          console.error("Project raw data not loaded");
          ToastQueue.negative("Project data not loaded. Please refresh.", {
            timeout: 3000,
          });
          return false;
        }
        // Prepare immutable updates for all rows
        const updatedRowsData = projectRawData.current.ROWS.map(
          (record: any) => {
            return {
              ...record.ITEM,
              currency: tempCurrency,
              purchaseCurrency: tempCurrency,
              currencyRate: rateToUse,
            };
          },
        );
        //console.log("updatedRowsData", updatedRowsData);
        // Update all project items in parallel for better performance
        const updatePromises = updatedRowsData.map((record: any) => {
          const generatedItem = generateProjectProductItem(
            record.folder,
            record.project,
            "",
            record.part_number,
            record.product_name,
            record.quantity,
            record.unit_price,
            record.total_price,
            record.totalAmountWithVat,
            tempCurrency, // Use the new currency
            record.vatValue,
            record.purchasePrice,
            record.attrib0,
            record.attrib0_id,
            record.attrib1,
            record.attrib1_id,
            record.attrib2,
            record.attrib2_id,
            record.supplier,
            record.refId,
            record.billingDate,
            record.billing_status,
          );

          return itemService.updateProjectItem(
            new UpdateProjectItemRequest(store.Server, store.SessionId, {
              ...generatedItem,
              ...record,
              currency: tempCurrency, // Explicitly override with new currency
              purchaseCurrency: tempCurrency, // Also update purchase currency
              currencyRate: rateToUse, // Explicitly set the new rate
            }),
          );
        });

        // Wait for all updates to complete
        await Promise.all(updatePromises);

        // Update state only after all API calls succeed
        setRowsData(updatedRowsData);
        dispatch({ type: "UPDATE_CURRENCY_RATE", rate: rateToUse });
        dispatch({ type: "UPDATE_TEMP_RATE", rate: rateToUse });
        dispatch({ type: "UPDATE_SELECTED_CURRENCY", currency: tempCurrency });
        dispatch({
          type: "UPDATE_CALCULATION",
          field: "currency",
          value: tempCurrency,
        });
        if (updateFinal && pendingExpectedCloseDate) {
          dispatch({
            type: "UPDATE_FINAL_DATE",
            date: pendingExpectedCloseDate,
          });
        }

        // Show success message
        updateFinal &&
          ToastQueue.positive("Exchange rate updated successfully", {
            timeout: 3000,
          });

        // Reload fresh data from server
        await Promise.all([
          loadProjectItemCalculation(String(selectedItem.id)),
          loadProjectDetails(selectedItem.id as number),
          loadProjectItemGridData(),
        ]);

        return true;
      } catch (error) {
        console.error("Error updating exchange rate:", error);
        ToastQueue.negative(
          "Failed to update exchange rate. Please try again.",
          {
            timeout: 3000,
          },
        );
        return false;
      } finally {
        setShowLoader(false);
      }
    },
    [
      selectedItem?.id,
      store.Server,
      store.SessionId,
      projectItemCalculation,
      tempCurrency,
      tempRate,
      pendingExpectedCloseDate,
      itemService,
      loadProjectItemCalculation,
      loadProjectDetails,
      loadProjectItemGridData,
    ],
  );

  const updateProbabilityValue = useCallback(
    async (newProbability: string): Promise<boolean> => {
      if (!selectedItem?.id) {
        console.error("No selected item ID available");
        return false;
      }

      try {
        const resProjectItem = await itemService.updateProjectItemCalculation(
          new UpdateProjectItemCalculationRequest(
            store.Server,
            store.SessionId,
            projectItemCalculation?.id?.toString() || "",
            [
              {
                ...projectItemCalculation,
                projectId: selectedItem?.id?.toString() || "",
                probability: newProbability,
                modified: new Date(),
              } as any,
            ],
          ),
        );
        if (resProjectItem?.result === "OK") {
          ToastQueue.positive("Probability updated successfully", {
            timeout: 3000,
          });
        } else {
          ToastQueue.negative("Failed to update probability", {
            timeout: 3000,
          });
        }
        return true;
      } catch (error) {
        console.error("Error updating probability:", error);
        ToastQueue.negative("Failed to update probability", { timeout: 3000 });
        return false;
      }
    },
    [
      selectedItem?.id,
      itemService,
      store.Server,
      store.SessionId,
      projectItemCalculation,
    ],
  );

  const handleExpectedCloseDateChange = useCallback((date: any) => {
    const newDate = moment(date.toString()).format("YYYY-MM-DD");
    setPendingExpectedCloseDate(newDate);
    setShowExchangeRateDialog(true);
  }, []);

  const handleExchangeRateDialogYes = useCallback(
    async (shouldUpdateRate: boolean) => {
      try {
        if (shouldUpdateRate) {
          // Yes: Update exchange rate to the selected date, then update both date and rate
          if (projectItemCalculation.currency !== "CZK") {
            const currency_id = currencies.find(
              (cur: any) => cur.code === projectItemCalculation.currency,
            )?.id;

            if (currency_id && pendingExpectedCloseDate) {
              const newRate = await getCurrencyRate(
                currency_id,
                pendingExpectedCloseDate + "T00:00:00",
              );
              dispatch({ type: "UPDATE_TEMP_RATE", rate: newRate.toString() });
              // Pass the new rate directly to avoid stale closure
              await updateExchangeRate(true, newRate.toString());
            }
          } else {
            // If CZK, just update the date
            await updateExchangeRate(true);
          }
        } else {
          // No: Update only the date, keep current exchange rate
          if (!selectedItem?.id) {
            ToastQueue.negative("No project selected", { timeout: 3000 });
            return;
          }

          setShowLoader(true);
          // Use pendingExpectedCloseDate if available, otherwise keep current finalDate
          const finalDateToUse =
            pendingExpectedCloseDate || projectItemCalculation.finalDate;

          const resProjectItem = await itemService.updateProjectItemCalculation(
            new UpdateProjectItemCalculationRequest(
              store.Server,
              store.SessionId,
              projectItemCalculation?.id?.toString() || "",
              [
                {
                  ...projectItemCalculation,
                  currency: projectItemCalculation.currency,
                  finalDate: finalDateToUse,
                  projectId: selectedItem.id.toString(),
                  modified: new Date(),
                } as any,
              ],
            ),
          );

          if (resProjectItem?.result === "OK") {
            if (pendingExpectedCloseDate) {
              dispatch({
                type: "UPDATE_FINAL_DATE",
                date: pendingExpectedCloseDate,
              });
            }
            ToastQueue.positive("Expected close date updated successfully", {
              timeout: 3000,
            });
            await loadProjectItemCalculation(String(selectedItem.id));
          } else {
            ToastQueue.negative("Failed to update expected close date", {
              timeout: 3000,
            });
          }
          setShowLoader(false);
        }
      } catch (error) {
        console.error("Error in handleExchangeRateDialogYes:", error);
        ToastQueue.negative("Failed to update", { timeout: 3000 });
        setShowLoader(false);
      } finally {
        setShowExchangeRateDialog(false);
      }
    },
    [
      updateExchangeRate,
      projectItemCalculation,
      currencies,
      getCurrencyRate,
      pendingExpectedCloseDate,
      selectedItem,
      itemService,
      store,
      loadProjectItemCalculation,
    ],
  );

  // Currency rate dialog handlers
  const handleOpenCurrencyRateDialog = useCallback(() => {
    setShowCurrencyRateDialog(true);
  }, []);

  const handleApplyCurrencyRate = useCallback(async () => {
    try {
      setShowLoader(true);
      setShowCurrencyRateDialog(false);

      // Call updateExchangeRate with false to not update final date
      const success = await updateExchangeRate(false, tempRate);

      if (success) {
        ToastQueue.positive("Currency and exchange rate updated successfully", {
          timeout: 3000,
        });
      }
    } catch (error) {
      console.error("Error applying currency rate:", error);
      ToastQueue.negative("Failed to update currency rate", { timeout: 3000 });
      setShowLoader(false);
    }
  }, [updateExchangeRate, tempRate]);

  const handleCancelCurrencyRate = useCallback(() => {
    dispatch({ type: "CANCEL_CURRENCY_RATE" });
    setShowCurrencyRateDialog(false);
  }, []);

  // Calculate Deal value in CZK
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const dealValueCZK = useMemo(() => {
    const dealValue = parseFloat(
      projectItemCalculation?.amountWithoutVat || "0",
    );
    const rate = parseFloat(currencyRate || "1");
    if (projectItemCalculation?.currency === "CZK") {
      return dealValue;
    }
    return dealValue * rate;
  }, [
    projectItemCalculation?.amountWithoutVat,
    projectItemCalculation?.currency,
    currencyRate,
  ]);

  const handleRowClose = (): void => {
    // setSelectedRow(defaultRowData);
    // setShowAddEditRow(false);
    // deviceInfo.shouldUseMobileLayout() && setIsOpen(false);
    // handleUnselect();
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleUnselect = () => {
    //setSelectedKeys(new Set());
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const addRecord = async (record: any) => {
    try {
      const updatedRowsData = {
        ...record,
        id: Math.random().toString(36).substring(7),
        unitPriceCurr:
          formatDecimalToString(record.unitPrice) + " " + record.currency,
        totalPriceCurr:
          formatDecimalToString(
            Number.parseFloat(record.totalPrice).toFixed(2),
          ) +
          " " +
          record.currency,
      };

      // Add the new record to the updated rows data
      const newRowsData = [...rowsData, updatedRowsData];
      // console.log('newRowsData', newRowsData);
      // Update the state with the new rows data
      setRowsData(newRowsData);
      handleRowClose();

      //console.log('record', record);

      let result = await itemService.createProjectItem(
        new CreateProjectItemRequest(
          store.Server,
          store.SessionId,
          generateBillingItem(
            String(selectedItem?.parent_id),
            String(selectedItem?.id),
            record.partID,
            record.productName,
            record.quantity,
            record.unitPrice,
            record.totalPrice,
            record.totalPriceWithVat || 0,
            record.currency,
            record.vatValue || 0,
            record.purchasePrice,
          ),
        ),
      );

      // console.log('result', result);
      if (result.result === "OK") {
        handleRowClose();
        ToastQueue.positive("Record saved successfully", { timeout: 3000 });
      } else {
        ToastQueue.negative(result.EXCEPTION.reason, { timeout: 3000 });
      }
    } catch (error: any) {
      ToastQueue.negative(error.message, { timeout: 3000 });
      throw error;
    } finally {
      setShowLoader(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const updateRecord = async (record: any) => {
    try {
      record.unitPriceCurr =
        formatDecimalToString(record.unitPrice) + " " + record.currency;
      record.totalPriceCurr =
        formatDecimalToString(Number.parseFloat(record.totalPrice).toFixed(2)) +
        " " +
        record.currency;
      const updatedRowsData = rowsData.map((row: any) =>
        row.id === record.id ? record : row,
      );
      //console.log('updatedRowsData', updatedRowsData);
      setRowsData(updatedRowsData);

      handleRowClose();

      setShowLoader(true);
      //console.log('update record', record);

      //console.log('itemRow', itemRow);
      let result = await itemService.updateProjectItem(
        new UpdateProjectItemRequest(
          store.Server,
          store.SessionId,
          generateBillingItem(
            String(selectedItem?.parent_id),
            String(selectedItem?.id),
            record.partID,
            record.productName,
            record.quantity,
            record.unitPrice,
            record.totalPrice,
            record.totalAmountWithVat || 0,
            record.currency,
            record.vatValue || 0,
            record.purchasePrice,
            record.id,
          ),
        ),
      );

      // console.log('result', result);
      if (result.result === "OK") {
        handleRowClose();
        ToastQueue.positive(
          t("record_updated_successfully", { ns: "layout_components" }),
          { timeout: 3000 },
        );
      } else {
        ToastQueue.negative(result.EXCEPTION.reason, { timeout: 3000 });
      }
    } catch (error) {
    } finally {
      setShowLoader(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const deleteRecord = async (record: any) => {
    try {
      setShowLoader(true);
      //console.log('record', record);

      let result = await itemService.deleteProjectItem(
        new DeleteProjectItemRequest(store.Server, store.SessionId, record.id),
      );

      // console.log('result', result);
      if (result.result === "OK") {
        handleRowClose();
        ToastQueue.positive("Record deleted successfully", { timeout: 3000 });
      } else {
        ToastQueue.negative(result.EXCEPTION.reason, { timeout: 3000 });
      }
    } catch (error) {
    } finally {
      setShowLoader(false);
    }
  };

  if (showLoader) {
    return (
      <Flex width="100%" justifyContent={"center"} marginTop={10}>
        <ProgressCircle aria-label="Loading…" isIndeterminate />
      </Flex>
    );
  } else {
    return (
      <>
        <View UNSAFE_className="detail">
          <View
            backgroundColor="gray-100"
            padding={{ base: "size-150", S: "size-200", M: "size-200" }}
            UNSAFE_style={{ marginTop: "15px", borderRadius: "12px" }}
          >
            <Flex direction="column" gap={{ base: "size-200", M: "size-200" }}>
              {/* Row 1: Block 1 (Expected close date, Probability, Currency) | Block 2 (Deal value CZK, Deal value) */}
              <View
                UNSAFE_style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                  gap: "24px",
                  alignItems: "start",
                }}
              >
                {/* Block 1: Expected close date, Probability, Currency */}
                <View
                  UNSAFE_style={{
                    display: "grid",
                    gridTemplateColumns: isMobile
                      ? "1fr"
                      : "minmax(140px, 1.5fr) minmax(80px, 0.8fr) minmax(70px, 0.6fr)",
                    gap: "16px",
                    alignItems: "end",
                  }}
                >
                  <View>
                    <Provider locale="en-US">
                      <DatePicker
                        label="Expected close date"
                        width="100%"
                        value={
                          projectItemCalculation?.finalDate
                            ? parseDate(
                                moment(
                                  projectItemCalculation?.finalDate,
                                  "YYYY-MM-DD",
                                ).format("YYYY-MM-DD"),
                              )
                            : undefined
                        }
                        onChange={handleExpectedCloseDateChange}
                      />
                    </Provider>
                  </View>

                  <View>
                    <NumericTextField
                      label="Probability"
                      width="100%"
                      value={
                        projectItemCalculation?.probability
                          ?.toString()
                          .replace("%", "")
                          .trim() || ""
                      }
                      onChange={(value: any) => {
                        dispatch({
                          type: "UPDATE_CALCULATION",
                          field: "probability",
                          value,
                        });
                        setProbabilityTerm(value);
                      }}
                      prefix="%"
                    />
                  </View>

                  <View>
                    <TextField
                      label="Currency"
                      width="100%"
                      value={selectedCurrency || "CZK"}
                      UNSAFE_className={styles.disabled}
                    />
                  </View>
                </View>

                {/* Block 2: Deal value CZK, Deal value + Exchange Rate */}
                <Flex direction="column">
                  <View
                    UNSAFE_style={{
                      display: "grid",
                      gridTemplateColumns: isMobile
                        ? "1fr"
                        : selectedCurrency !== "CZK"
                          ? "1fr 1fr"
                          : "1fr",
                      gap: "16px",
                      alignItems: "end",
                    }}
                  >
                    {selectedCurrency !== "CZK" && (
                      <View>
                        <NumericTextField
                          label="Deal value CZK"
                          width="100%"
                          value={formatDecimalToString(
                            projectItemCalculation?.amountWithoutVatBccy
                              ? Number.parseFloat(
                                  projectItemCalculation?.amountWithoutVatBccy,
                                ).toFixed(2)
                              : "0",
                          )}
                          isReadOnly
                          thousandsSeparator={true}
                        />
                      </View>
                    )}

                    <View>
                      <Flex direction="row" alignItems="end" gap="size-100">
                        <View flex="1">
                          <NumericTextField
                            label="Deal value"
                            width="100%"
                            value={
                              projectItemCalculation?.amountWithoutVat
                                ? formatDecimalToString(
                                    Number.parseFloat(
                                      projectItemCalculation?.amountWithoutVat,
                                    ).toFixed(2),
                                  )
                                : "0"
                            }
                            onChange={(value: any) => {
                              dispatch({ type: "UPDATE_DEAL_VALUE", value });
                            }}
                            prefix={projectItemCalculation?.currency || "CZK"}
                            thousandsSeparator={true}
                          />
                        </View>
                        {changeFlags.isDealValueChanged && (
                          <Flex direction="row" gap="size-50">
                            <ActionButton
                              id="btn_handleSaveDealValue"
                              isQuiet
                              onPress={handleSaveDealValue}
                              UNSAFE_style={{
                                minWidth: "auto",
                                padding: "4px",
                              }}
                            >
                              <Checkmark size="S" />
                            </ActionButton>
                            <ActionButton
                              id="btn_handleCancelDealValue"
                              isQuiet
                              onPress={handleCancelDealValue}
                              UNSAFE_style={{
                                minWidth: "auto",
                                padding: "4px",
                              }}
                            >
                              <Close size="S" />
                            </ActionButton>
                          </Flex>
                        )}
                      </Flex>
                    </View>
                  </View>

                  {/* Exchange Rate Row - Under Deal value fields */}
                  <Flex
                    direction="row"
                    alignItems="center"
                    justifyContent="end"
                    alignContent={"end"}
                  >
                    <Text
                      UNSAFE_style={{
                        color: "rgb(25, 118, 210)",
                        fontSize: "14px",
                      }}
                    >
                      Exchange rate{" "}
                      {rowsData.length > 0
                        ? formatDecimalToString(
                            parseFloat(currencyRate).toFixed(3),
                          )
                        : "1.000"}
                    </Text>
                    <ActionButton
                      isQuiet
                      onPress={handleOpenCurrencyRateDialog}
                      UNSAFE_style={{
                        minWidth: "auto",
                        color: "rgb(25, 118, 210)",
                        fontSize: "14px",
                        textDecoration: "underline",
                      }}
                    >
                      <Text
                        UNSAFE_style={{
                          color: "rgb(25, 118, 210)",
                          fontSize: "14px",
                          textDecoration: "underline",
                        }}
                      >
                        [edit]
                      </Text>
                    </ActionButton>
                  </Flex>
                </Flex>
              </View>

              <Divider size="S" />

              {/* Row 2: Block 3 (Margin/Loss graphic) | Block 4 (Purchase cost CZK, Purchase cost) */}
              <View
                UNSAFE_style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                  gap: "24px",
                  alignItems: "end",
                }}
              >
                {/* Block 3: Margin Section */}
                <View>
                  {showMarginGraphic ? (
                    <div
                      style={{ width: "100%", cursor: "pointer" }}
                      onClick={() => setShowMarginGraphic(false)}
                    >
                      <Flex
                        direction={{ base: "column", S: "row" }}
                        justifyContent="space-between"
                        gap="size-100"
                        marginBottom="size-100"
                      >
                        <Flex
                          direction="row"
                          gap="size-100"
                          alignItems="center"
                        >
                          <Text
                            UNSAFE_style={{
                              fontSize: "14px",
                              fontWeight: "bold",
                            }}
                          >
                            {grossProfitCZK >= 0 ? "Gross Profit:" : "Loss:"}
                          </Text>
                          <Text
                            UNSAFE_style={{
                              fontWeight: "bold",
                              color:
                                grossProfitCZK >= 0 ? "#1976d2" : "#d32f2f",
                              fontSize: "14px",
                            }}
                          >
                            CZK {formatCurrency(grossProfitCZK)}
                          </Text>
                        </Flex>
                        <Flex
                          direction="row"
                          gap="size-100"
                          alignItems="center"
                        >
                          <Text UNSAFE_style={{ fontSize: "14px" }}>
                            Margin:
                          </Text>
                          <Text
                            UNSAFE_style={{
                              fontWeight: "bold",
                              color:
                                marginPercentage >= 0 ? "#1976d2" : "#d32f2f",
                              fontSize: "14px",
                            }}
                          >
                            {formatCurrency(marginPercentage)}%
                          </Text>
                        </Flex>
                      </Flex>
                      <View
                        height="size-200"
                        borderRadius="large"
                        UNSAFE_style={{
                          position: "relative",
                          overflow: "hidden",
                          backgroundColor:
                            marginPercentage >= 0 ? "#e3f2fd" : "#ffebee",
                        }}
                      >
                        <View
                          UNSAFE_style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            backgroundColor:
                              marginPercentage >= 0 ? "#1976d2" : "#d32f2f",
                            width: `${barWidthPercentage}%`,
                            height: "100%",
                            transition:
                              "width 0.3s ease-in-out, background-color 0.3s ease-in-out",
                          }}
                        />
                      </View>
                    </div>
                  ) : (
                    <View
                      UNSAFE_style={{
                        display: "grid",
                        gridTemplateColumns: isMobile
                          ? "1fr"
                          : selectedCurrency !== "CZK"
                            ? "minmax(60px, 0.8fr) minmax(90px, 1fr) minmax(90px, 1fr) auto"
                            : "minmax(80px, 1fr) minmax(120px, 1.5fr) auto",
                        gap: "16px",
                        alignItems: "end",
                      }}
                    >
                      <View>
                        <NumericTextField
                          label="Margin %"
                          width="100%"
                          value={formatCurrency(marginPercentage)}
                          onChange={(value: any) => {
                            const purchaseCost =
                              (parseFloat(
                                projectItemCalculation.totalPurchase,
                              ) || 0) * (parseFloat(currencyRate) || 1);
                            const rate = parseFloat(currencyRate) || 1;
                            dispatch({
                              type: "UPDATE_MARGIN_PERCENTAGE",
                              value,
                              purchaseCost,
                              rate,
                            });
                          }}
                          prefix="%"
                        />
                      </View>
                      {selectedCurrency !== "CZK" && (
                        <View>
                          <NumericTextField
                            label="Margin CZK"
                            width="100%"
                            value={formatCurrency(marginValueCZK)}
                            isReadOnly
                            thousandsSeparator={true}
                          />
                        </View>
                      )}
                      <View>
                        <NumericTextField
                          label="Margin value"
                          width="100%"
                          value={formatCurrency(marginValue)}
                          onChange={(value: any) => {
                            const purchaseCost =
                              (parseFloat(
                                projectItemCalculation.totalPurchase,
                              ) || 0) * (parseFloat(currencyRate) || 1);
                            const rate = parseFloat(currencyRate) || 1;
                            dispatch({
                              type: "UPDATE_MARGIN_VALUE",
                              value,
                              purchaseCost,
                              rate,
                            });
                          }}
                          thousandsSeparator={true}
                        />
                      </View>
                      <View
                        UNSAFE_style={{
                          display: "flex",
                          alignItems: "flex-end",
                          height: "100%",
                        }}
                      >
                        {changeFlags.isMarginPercentageChanged ||
                        changeFlags.isMarginValueChanged ? (
                          <Flex direction="row" gap="size-50">
                            <ActionButton
                              id="btn_handleSaveMargin"
                              isQuiet
                              onPress={handleSaveMargin}
                              UNSAFE_style={{
                                minWidth: "auto",
                                padding: "4px",
                              }}
                            >
                              <Checkmark size="S" />
                            </ActionButton>
                            <ActionButton
                              id="btn_handleCancelMargin"
                              isQuiet
                              onPress={handleCancelMargin}
                              UNSAFE_style={{
                                minWidth: "auto",
                                padding: "4px",
                              }}
                            >
                              <Close size="S" />
                            </ActionButton>
                          </Flex>
                        ) : (
                          <ActionButton
                            isQuiet
                            onPress={() => setShowMarginGraphic(true)}
                            UNSAFE_style={{ minWidth: "auto", padding: "4px" }}
                          >
                            <GraphBarVertical size="S" />
                          </ActionButton>
                        )}
                      </View>
                    </View>
                  )}
                </View>

                {/* Block 4: Purchase Cost Section */}
                <View
                  UNSAFE_style={{
                    display: "grid",
                    gridTemplateColumns: isMobile
                      ? "1fr"
                      : selectedCurrency !== "CZK"
                        ? "1fr 1fr"
                        : "1fr",
                    gap: "16px",
                    alignItems: "end",
                  }}
                >
                  {selectedCurrency !== "CZK" && (
                    <View>
                      <NumericTextField
                        label="Purchase cost CZK"
                        width="100%"
                        value={
                          projectItemCalculation?.totalPurchase
                            ? formatDecimalToString(
                                (
                                  Number.parseFloat(
                                    projectItemCalculation?.totalPurchase,
                                  ) * (rowsData[0]?.currencyRate || 1)
                                ).toFixed(2),
                              )
                            : "0"
                        }
                        isReadOnly
                        thousandsSeparator={true}
                      />
                    </View>
                  )}
                  <Flex direction="row" alignItems="end" gap="size-100">
                    <View flex="1">
                      <NumericTextField
                        label="Purchase cost"
                        width="100%"
                        value={
                          projectItemCalculation?.totalPurchase
                            ? formatDecimalToString(
                                Number.parseFloat(
                                  projectItemCalculation?.totalPurchase,
                                ).toFixed(2),
                              )
                            : "0"
                        }
                        onChange={(value: any) => {
                          dispatch({ type: "UPDATE_PURCHASE_COST", value });
                        }}
                        prefix={projectItemCalculation?.currency || "CZK"}
                        thousandsSeparator={true}
                      />
                    </View>
                    {changeFlags.isPurchaseCostChanged && (
                      <Flex direction="row" gap="size-50">
                        <ActionButton
                          id="btn_handleSavePurchaseCost"
                          isQuiet
                          onPress={handleSavePurchaseCost}
                          UNSAFE_style={{ minWidth: "auto", padding: "4px" }}
                        >
                          <Checkmark size="S" />
                        </ActionButton>
                        <ActionButton
                          id="btn_handleCancelPurchaseCost"
                          isQuiet
                          onPress={handleCancelPurchaseCost}
                          UNSAFE_style={{ minWidth: "auto", padding: "4px" }}
                        >
                          <Close size="S" />
                        </ActionButton>
                      </Flex>
                    )}
                  </Flex>
                </View>
              </View>

              <Divider size="S" />

              {/* Row 3: Block 5 (Discount, Discount CZK, Discount value) | Block 6 (Catalog price CZK, Catalog price) */}
              <View
                UNSAFE_style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                  gap: "24px",
                  alignItems: "end",
                }}
              >
                {/* Block 5: Discount Section */}
                <View
                  UNSAFE_style={{
                    display: "grid",
                    gridTemplateColumns: isMobile
                      ? "1fr"
                      : selectedCurrency !== "CZK"
                        ? "minmax(60px, 0.8fr) minmax(90px, 1fr) minmax(180px, 1.4fr)"
                        : "minmax(80px, 1fr) minmax(200px, 1.5fr)",
                    gap: "16px",
                    alignItems: "end",
                    overflow: "visible",
                  }}
                >
                  <View>
                    <NumericTextField
                      label="Discount"
                      width="100%"
                      value={discountPercentageInput}
                      onChange={(value: any) => {
                        const catalogPrice =
                          parseFloat(projectItemCalculation.catalogPriceBccy) ||
                          0;
                        dispatch({
                          type: "UPDATE_DISCOUNT_PERCENTAGE",
                          value,
                          catalogPrice,
                        });
                      }}
                      prefix="%"
                    />
                  </View>

                  {selectedCurrency !== "CZK" && (
                    <View>
                      <NumericTextField
                        label="Discount CZK"
                        width="100%"
                        value={discountValueBccyInput}
                        onChange={(value: any) => {
                          const catalogPrice =
                            parseFloat(
                              projectItemCalculation.catalogPriceBccy,
                            ) || 0;
                          dispatch({
                            type: "UPDATE_DISCOUNT_VALUE",
                            value,
                            catalogPrice,
                          });
                        }}
                        thousandsSeparator={true}
                      />
                    </View>
                  )}

                  <Flex
                    direction="row"
                    alignItems="end"
                    gap="size-100"
                    UNSAFE_style={{ overflow: "visible" }}
                  >
                    <View flex="1" minWidth="size-1200">
                      <NumericTextField
                        label="Discount value"
                        width="100%"
                        value={discountValueInput}
                        onChange={(value: any) => {
                          const catalogPrice =
                            parseFloat(
                              projectItemCalculation.catalogPriceBccy,
                            ) || 0;
                          dispatch({
                            type: "UPDATE_DISCOUNT_VALUE_SELECTED_CCY",
                            value,
                            catalogPrice,
                          });
                        }}
                        thousandsSeparator={true}
                      />
                    </View>
                    {(changeFlags.isDiscountChanged ||
                      changeFlags.isDiscountValueChanged ||
                      changeFlags.isCatalogPriceChanged) && (
                      <Flex
                        direction="row"
                        gap="size-25"
                        flexShrink={0}
                        alignItems="center"
                        UNSAFE_style={{ minWidth: "56px", marginBottom: "6px" }}
                      >
                        <ActionButton
                          id="btn_handleSaveCatalogPrice"
                          isQuiet
                          onPress={handleSaveCatalogPrice}
                          UNSAFE_style={{
                            minWidth: "24px",
                            width: "24px",
                            padding: "2px",
                          }}
                        >
                          <Checkmark size="S" />
                        </ActionButton>
                        <ActionButton
                          id="btn_handleCancelCatalogPrice"
                          isQuiet
                          onPress={handleCancelCatalogPrice}
                          UNSAFE_style={{
                            minWidth: "24px",
                            width: "24px",
                            padding: "2px",
                          }}
                        >
                          <Close size="S" />
                        </ActionButton>
                      </Flex>
                    )}
                  </Flex>
                </View>

                {/* Block 6: Catalog Price Section */}
                <View
                  UNSAFE_style={{
                    display: "grid",
                    gridTemplateColumns: isMobile
                      ? "1fr"
                      : selectedCurrency !== "CZK"
                        ? "1fr 1fr"
                        : "1fr",
                    gap: "16px",
                    alignItems: "end",
                  }}
                >
                  {selectedCurrency !== "CZK" && (
                    <View>
                      <NumericTextField
                        label="Catalog price CZK"
                        width="100%"
                        value={
                          projectItemCalculation?.catalogPriceBccy
                            ? formatDecimalToString(
                                Number.parseFloat(
                                  projectItemCalculation?.catalogPriceBccy,
                                ).toFixed(2),
                              )
                            : "0"
                        }
                        isReadOnly
                        thousandsSeparator={true}
                      />
                    </View>
                  )}

                  <View>
                    <NumericTextField
                      label="Catalog price"
                      width="100%"
                      value={
                        projectItemCalculation?.catalogPrice
                          ? formatDecimalToString(
                              Number.parseFloat(
                                projectItemCalculation?.catalogPrice,
                              ).toFixed(2),
                            )
                          : "0"
                      }
                      onChange={(value: any) => {
                        dispatch({ type: "UPDATE_CATALOG_PRICE", value });
                      }}
                      isReadOnly
                      prefix={projectItemCalculation?.currency || "CZK"}
                      thousandsSeparator={true}
                    />
                  </View>
                </View>
              </View>

              <Divider size="S" />

              {/* Row 4: Block 7 (Provider) | Block 8 (Invoice To) */}
              <View
                UNSAFE_style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                  gap: "24px",
                }}
              >
                {/* Block 7: Provider */}
                <View>
                  <ComboBox
                    label="Provider"
                    width="100%"
                    placeholder={t("type_to_search", { ns: "recruitment" })}
                    items={providerList.items}
                    inputValue={providerInputValue}
                    onInputChange={(value) => {
                      setProviderInputValue(value);
                      providerList.setFilterText(value);
                    }}
                    loadingState={providerList.loadingState}
                    selectedKey={selectedProviderId}
                    onSelectionChange={(key) => {
                      if (key) {
                        const keyStr = String(key);
                        const selected: any = providerList.items.find(
                          (p: any) => p.id === keyStr,
                        );
                        if (selected) {
                          setProviderInputValue(selected.name);
                          if (selectedProviderId !== keyStr) {
                            setSelectedProviderId(keyStr);
                            updateProjectProvider(selected.id);
                          }
                        }
                      } else {
                        setSelectedProviderId(null);
                        setProviderInputValue("");
                      }
                    }}
                    allowsCustomValue
                    menuTrigger="input"
                  >
                    {(item: any) => <Item key={item.id}>{item.name}</Item>}
                  </ComboBox>
                </View>

                {/* Block 8: Invoice To */}
                <View>
                  <ComboBox
                    label="Invoice To"
                    width="100%"
                    placeholder={t("type_to_search", { ns: "recruitment" })}
                    items={invoiceToList.items}
                    inputValue={invoiceToInputValue}
                    onInputChange={(value) => {
                      setInvoiceToInputValue(value);
                      invoiceToList.setFilterText(value);
                    }}
                    loadingState={invoiceToList.loadingState}
                    selectedKey={selectedInvoiceToId}
                    onSelectionChange={(key) => {
                      if (key) {
                        const keyStr = String(key);
                        const selected: any = invoiceToList.items.find(
                          (i: any) => i.id === keyStr,
                        );
                        if (selected) {
                          setInvoiceToInputValue(selected.name);
                          if (selectedInvoiceToId !== keyStr) {
                            setSelectedInvoiceToId(keyStr);
                            updateProjectInvoiceTo(selected.id);
                          }
                        }
                      } else {
                        setSelectedInvoiceToId(null);
                        setInvoiceToInputValue("");
                      }
                    }}
                    allowsCustomValue
                    menuTrigger="input"
                  >
                    {(item: any) => <Item key={item.id}>{item.name}</Item>}
                  </ComboBox>
                </View>
              </View>
            </Flex>
          </View>

          {/* <Flex direction="row" justifyContent="space-between" gap="size-300" marginTop="size-300">
            <Flex direction="row" gap="size-200">
              <Button variant="secondary">
                <Text>Add</Text>
              </Button>
              <Button variant="negative">
                <Text>Delete</Text>
              </Button>
            </Flex>
            <Flex direction="row" gap="size-200">
              <Button variant="secondary">
                <Text>Save</Text>
              </Button>
              <Button variant="cta">
                <Text>Delivery Dashboard</Text>
              </Button>
            </Flex>
          </Flex> */}

          <View marginTop="size-225" position="relative">
            {/* <Flex direction="row" justifyContent="space-between" alignItems="center">
              <Flex direction="row" gap="size-200" alignItems="center">
                <Text>Total:</Text>
                <Text UNSAFE_style={{ fontWeight: 'bold' }}>1,000.00 CZK</Text>
              </Flex>
              <Flex direction="row" gap="size-200" alignItems="center">
                <Text>Total:</Text>
                <Text UNSAFE_style={{ fontWeight: 'bold' }}>1,000.00 CZK</Text>
              </Flex>
            </Flex> */}

            <View marginTop="size-225">
              <View position={"relative"} width={"100%"}>
                <Flex direction={"column"} gap={"size-150"} width={"100%"}>
                  <Flex
                    direction={"row"}
                    alignItems={"start"}
                    justifyContent={"start"}
                  >
                    <Content
                      position={"relative"}
                      UNSAFE_className={styles.heading_text}
                    >
                      {componentTitle}
                    </Content>
                    <Flex
                      direction={"row"}
                      alignItems={"center"}
                      justifyContent={"center"}
                      gap={"size-100"}
                      UNSAFE_className={styles.icon_add_parent}
                    >
                      <Content>
                        <></>
                      </Content>
                    </Flex>
                  </Flex>
                  <Flex
                    direction={"column"}
                    width={"100%"}
                    UNSAFE_style={{ maxWidth: "100%", overflowX: "auto" }}
                  >
                    {/* Add/Cancel buttons when adding new item */}
                    {isAddingItem && (
                      <Flex
                        direction="row"
                        gap="size-200"
                        marginBottom="size-200"
                      >
                        <Button
                          variant="cta"
                          onPress={handleSaveNewItem}
                          UNSAFE_style={{
                            backgroundColor: "#fff",
                            color: "#1976d2",
                            border: "1px solid #1976d2",
                            padding: "6px 16px",
                            borderRadius: "8px",
                          }}
                        >
                          <Text>+ Add</Text>
                        </Button>
                        <Button
                          variant="secondary"
                          onPress={handleCancelAddItem}
                          UNSAFE_style={{
                            backgroundColor: "#fff",
                            color: "#d32f2f",
                            border: "1px solid #d32f2f",
                            padding: "6px 16px",
                            borderRadius: "8px",
                          }}
                        >
                          <Text>Cancel</Text>
                        </Button>
                      </Flex>
                    )}

                    <div ref={componentRef}>
                      {/* New Item Add Mode Grid */}
                      {isAddingItem && newItemData && (
                        <View marginBottom="size-200">
                          <TreeGridComponent
                            title=""
                            gridId="new-item-grid"
                            columns={[
                              {
                                key: "productName",
                                header: t("product_name", {
                                  ns: "layout_components",
                                }),
                                width: 250,
                                editable: true,
                                editorType: "combobox",
                                isTreeColumn: true,
                                showFolderIcon: true,
                                comboBoxConfig: {
                                  placeholder:
                                    "Search products by name or SKU...",
                                  menuWidth: 450,
                                  debounceMs: 250,
                                  minSearchLength: 2,
                                  loadItems: searchProducts,
                                  groupBy: (item) => item.category || "Other",
                                  getDisplayValue: (item) =>
                                    item.productName || item.label,
                                  getValue: (item) =>
                                    item.productName || item.label,
                                  onSelectionChange: (item, row) => {
                                    if (!item) return {};
                                    return {
                                      ...item,
                                      productName:
                                        item.productName || item.label,
                                      description:
                                        item.productName || item.label,
                                      refId: item.sku || "",
                                      partID: item.productId,
                                      unitPrice: item.unitPrice || 0,
                                      amountWithVat:
                                        item.amountWithVat ||
                                        item.catalogPrice ||
                                        0,
                                      vatValue: item.vatValue || 0,
                                      purchasePrice: item.purchasePrice || 0,
                                      currency: item.currency || "CZK",
                                      totalPrice:
                                        (row.data.quantity || 1) *
                                        (item.unitPrice || 0),
                                    };
                                  },
                                },
                              },
                              {
                                key: "description",
                                header: t("description", {
                                  ns: "layout_components",
                                }),
                                width: 200,
                                editable: true,
                                editorType: "text",
                              },
                              {
                                key: "quantity",
                                header: t("quantity", {
                                  ns: "layout_components",
                                }),
                                width: 100,
                                editable: true,
                                editorType: "number",
                                align: "right",
                                numberFormat: {
                                  decimals: 2,
                                },
                              },
                              {
                                key: "unitPrice",
                                header: t("unit_sales_price", {
                                  ns: "layout_components",
                                }),
                                width: 150,
                                editable: false,
                                align: "right",
                                format: (value: any, row: TreeGridRow) =>
                                  `${formatDecimalToString(
                                    parseFloat(value || 0).toFixed(2),
                                  )} ${row.data.currency || "CZK"}`,
                              },
                              {
                                key: "totalPrice",
                                header: t("total_sales_price", {
                                  ns: "layout_components",
                                }),
                                width: 150,
                                editable: false,
                                align: "right",
                                format: (value: any, row: TreeGridRow) =>
                                  `${formatDecimalToString(
                                    parseFloat(value || 0).toFixed(2),
                                  )} ${row.data.currency || "CZK"}`,
                              },
                            ]}
                            data={[
                              {
                                id: newItemData.id,
                                parentId: null,
                                depth: 0,
                                childrenIds: [],
                                isExpanded: false,
                                rowType: "product" as const,
                                sortOrder: 0,
                                data: newItemData,
                              },
                            ]}
                            showSelection={false}
                            showSelectAll={false}
                            enableDragDrop={false}
                            enableKeyboardNav={true}
                            enableInlineEdit={true}
                            pageSize={1}
                            indentPerLevel={32}
                            maxDepth={1}
                            showGridLines={true}
                            showHover={true}
                            showToolbar={false}
                            showDeleteIcon={false}
                            showInfoIcon={false}
                            defaultEditingRowId={newItemData.id}
                            onRowUpdate={handleNewItemRowUpdate}
                            onCellValueChange={(rowId, columnKey, value) => {
                              // Capture cell value changes immediately during typing
                              setNewItemData((prev: any) => {
                                const updated = { ...prev, [columnKey]: value };
                                // Recalculate totalPrice if quantity changes
                                if (columnKey === "quantity") {
                                  const qty = parseFloat(value) || 1;
                                  updated.quantity = qty;
                                  updated.totalPrice =
                                    qty * (prev.unitPrice || 0);
                                }
                                return updated;
                              });
                            }}
                            onMultiFieldUpdate={(rowId, changes) => {
                              // Immediately update newItemData when ComboBox selects a product
                              const quantity =
                                changes.quantity ?? newItemData.quantity ?? 1;
                              const unitPrice =
                                changes.unitPrice ?? newItemData.unitPrice ?? 0;
                              const totalPrice = quantity * unitPrice;

                              setNewItemData((prev: any) => {
                                // Only update description if it's coming from product selection
                                // (when productName is being changed), otherwise preserve user's edit
                                const newDescription =
                                  changes.description !== undefined
                                    ? changes.description
                                    : prev.description;

                                return {
                                  ...prev,
                                  ...changes,
                                  quantity,
                                  unitPrice,
                                  totalPrice,
                                  description: newDescription,
                                };
                              });
                            }}
                          />
                        </View>
                      )}

                      {rowsData.length > 0 && (
                        /**
                         <TableView
                         UNSAFE_className="tblLayoutComponent"
                         aria-label="Project Item"
                         onSelectionChange={handleRowClick}
                         selectionMode="single"
                         selectionStyle="highlight"
                         width={"100%"}
                         minHeight={"80px"}
                         maxHeight={{ base: "150px", M: "180px", L: "180px" }}
                         // maxWidth={componentWidth}
                         marginBottom={"size-250"}
                         UNSAFE_style={{ maxWidth: "100%" }}
                       >
                         <TableHeader
                           columns={isMobile ? columnsDataMobile : columnsData}
                         >
                           {(column) => (
                             <Column
                               showDivider
                               key={column.key}
                               width={column?.width}
                             >
                               {column.name}
                             </Column>
                           )}
                         </TableHeader>
                         <TableBody items={rowsData}>
                           {(item) => (
                             <Row>
                               {(columnKey) => {
                                 const value = (item as any)[String(columnKey)];
                                 return <Cell>{value}</Cell>;
                               }}
                             </Row>
                           )}
                         </TableBody>
                       </TableView>
                         */
                        <TreeGridComponent
                          title="Project Items"
                          columns={(isMobile
                            ? columnsDataMobile
                            : columnsData
                          ).map(
                            (col: ColumnDefinition) =>
                              ({
                                key: col.key,
                                header: col.name,
                                width:
                                  col.width ||
                                  (col.key === "productName" ? 200 : undefined),
                                editable: false,
                                isTreeColumn: col.key === "productName",
                                showFolderIcon: col.key === "productName",
                                align: [
                                  "quantity",
                                  "soldQuantity",
                                  "remainingSold",
                                  "purchaseQuantity",
                                  "remainingPurchase",
                                  "ratio",
                                ].includes(col.key)
                                  ? "right"
                                  : "left",
                              }) as TreeGridColumn,
                          )}
                          data={rowsData.map(
                            (item: any, index: number) =>
                              ({
                                id: item.id || `row-${index}`,
                                parentId: item.parentId || null,
                                depth: item.depth || 0,
                                childrenIds: item.childrenIds || [],
                                isExpanded: item.isExpanded !== false,
                                rowType: "product" as const,
                                sortOrder: item.sortOrder ?? index,
                                data: item,
                              }) as TreeGridRow,
                          )}
                          showSelection={true}
                          showSelectAll={true}
                          enableDragDrop={false}
                          enableKeyboardNav={true}
                          enableInlineEdit={false}
                          onSelectionChange={(selectedIds: string[]) => {
                            // Update selection state using reducer
                            dispatchSelection({
                              type: "SET_SELECTED_IDS",
                              selectedIds: selectedIds,
                            });

                            // Log selected rows data for debugging
                            if (selectedIds && selectedIds.length > 0) {
                              // Selection exists, rows can be accessed via rowsData.filter
                            } else {
                              console.log("Selection cleared");
                            }
                          }}
                          pageSize={20}
                          indentPerLevel={32}
                          maxDepth={3}
                          showGridLines={true}
                          showHover={true}
                          showToolbar={false}
                          className="project-item-grid"
                          showDeleteIcon={true}
                          showInfoIcon={false}
                          deleteConfirmationTitle="Remove Project Item"
                          deleteConfirmationMessage="Are you sure you want to delete this project item? This action cannot be undone."
                          onDeleteClick={(rowId: string) =>
                            handleDeleteRow(rowId)
                          }
                        />
                      )}
                    </div>
                  </Flex>
                </Flex>
              </View>
            </View>

            {/* Add Item button - hidden when in add mode */}
            {!isAddingItem && (
              <ActionButton
                isQuiet
                marginTop="size-225"
                onPress={handleStartAddItem}
                UNSAFE_style={{
                  color: "#0070f3",
                  border: "1px solid #0070f3",
                  borderRadius: "8px",
                  padding: "8px 16px",
                  fontWeight: 600,
                }}
              >
                <Text>+ Add item</Text>
              </ActionButton>
            )}

            <Flex
              direction={{ base: "column", S: "row" }}
              justifyContent="end"
              gap="size-200"
              marginTop="size-300"
              wrap="wrap"
            >
              <Button
                variant="cta"
                UNSAFE_style={{
                  backgroundColor: "#e3f2fd",
                  color: "#1976d2",
                  borderRadius: "8px",
                  borderColor: "#DFEFFD",
                }}
                onPress={handleCreateEstimate}
              >
                <Text>
                  {selectedIds.length > 0
                    ? `Create Estimate (${selectedIds.length})`
                    : "Create Estimate"}
                </Text>
              </Button>
              <Button
                variant="cta"
                UNSAFE_style={{
                  backgroundColor: "#e3f2fd",
                  color: "#1976d2",
                  borderRadius: "8px",
                  borderColor: "#DFEFFD",
                }}
                onPress={handleCreateSalesOrder}
              >
                <Text>
                  {selectedIds.length > 0
                    ? `Create Sales Order (${selectedIds.length})`
                    : "Create Sales Order"}
                </Text>
              </Button>
              <Button
                variant="cta"
                UNSAFE_style={{
                  backgroundColor: "#e3f2fd",
                  color: "#1976d2",
                  borderRadius: "8px",
                  borderColor: "#DFEFFD",
                }}
                onPress={handleCreateInvoice}
              >
                <Text>
                  {selectedIds.length > 0
                    ? `Create Invoice (${selectedIds.length})`
                    : "Create Invoice"}
                </Text>
              </Button>
            </Flex>
          </View>
        </View>

        {/* <View position={'relative'} width={'100%'} minHeight={'220px'}>
          <Flex direction={'column'} gap={'size-150'} position={'absolute'} top={0} left={0} right={0} width={'100%'} UNSAFE_style={{ zIndex: 1 }}>
            <Flex direction={'row'} alignItems={'start'} justifyContent={'start'}>
              <Content position={'relative'} UNSAFE_className={styles.heading_text}>
                {componentTitle}
              </Content>
              <Flex direction={'row'} alignItems={'center'} justifyContent={'center'} gap={'size-100'} UNSAFE_className={styles.icon_add_parent}>
                <Content>
                  <></>
                </Content>
              </Flex>
            </Flex>
            <Flex direction={'column'} width={'100%'} UNSAFE_style={{ maxWidth: '100%' }}>
              <div ref={componentRef}>
                {rowsData.length > 0 && (
                  <TableView
                    UNSAFE_className="tblLayoutComponent"
                    aria-label="Project Item"
                    onSelectionChange={handleRowClick}
                    selectionMode="single"
                    selectionStyle="highlight"
                    width={'100%'}
                    minHeight={'80px'}
                    maxHeight={{ base: '180px', L: '180px' }}
                    maxWidth={componentWidth}
                    marginBottom={'size-250'}
                    UNSAFE_style={{ maxWidth: '100%' }}
                  >
                    <TableHeader columns={isMobile ? columnsDataMobile : columnsData}>
                      {column => (
                        <Column showDivider key={column.key} width={column?.width}>
                          {column.name}
                        </Column>
                      )}
                    </TableHeader>
                    <TableBody items={rowsData}>{item => <Row>{columnKey => <Cell>{(item as any)[String(columnKey)]}</Cell>}</Row>}</TableBody>
                  </TableView>
                )}
              </div>
            </Flex>
          </Flex>
        </View> */}
        {/* Exchange Rate Update Dialog */}
        <DialogTrigger
          isOpen={showExchangeRateDialog}
          onOpenChange={setShowExchangeRateDialog}
        >
          <ActionButton UNSAFE_style={{ display: "none" }}>
            Hidden Trigger
          </ActionButton>
          {(close) => (
            <Dialog size="L">
              <Flex UNSAFE_className={styles.delete_pop_up_parent}>
                <Content UNSAFE_className={styles.delete_pop_up_header}>
                  Do you want to update exchange rate?
                </Content>
                <View UNSAFE_className={styles.delete_pop_up_line}></View>
                <View UNSAFE_className={styles.delete_pop_up_warning_text}>
                  If you agree, we will update exchange rate of the deal to
                  latest system stored rate.
                </View>
                <View UNSAFE_className={styles.buttons_parent}>
                  <Button
                    variant="secondary"
                    onPress={() => {
                      close();
                    }}
                    UNSAFE_className={styles.button_cancel}
                  >
                    <span className={styles.delete_pop_up_cancel_text}>
                      Cancel
                    </span>
                  </Button>
                  <Button
                    variant="primary"
                    onPress={() => {
                      handleExchangeRateDialogYes(false);
                      close();
                    }}
                    UNSAFE_className={styles.button_cancel}
                  >
                    <span className={styles.delete_pop_up_cancel_text}>No</span>
                  </Button>
                  <Button
                    variant="negative"
                    onPress={() => {
                      handleExchangeRateDialogYes(true);
                      close();
                    }}
                    UNSAFE_className={styles.button_confirm}
                  >
                    <span className={styles.delete_pop_up_confirm_text}>
                      Yes
                    </span>
                  </Button>
                </View>
                <View UNSAFE_className={styles.ellipse_div}>
                  <View UNSAFE_className={styles.delete_alert_icon}>
                    <AlertIconFill size={72} />
                  </View>
                </View>
              </Flex>
            </Dialog>
          )}
        </DialogTrigger>

        {/* Currency Rate Dialog */}
        <DialogTrigger
          isOpen={showCurrencyRateDialog}
          onOpenChange={setShowCurrencyRateDialog}
        >
          <ActionButton UNSAFE_style={{ display: "none" }}>
            Hidden Trigger
          </ActionButton>
          {(close) => (
            <Dialog>
              <Heading>Currency Rate</Heading>
              <Divider />
              <DialogContent>
                <Flex direction="column" gap="size-200">
                  <Picker
                    label="Currency"
                    selectedKey={tempCurrency}
                    onSelectionChange={async (key) => {
                      setShowLoader(true);
                      dispatch({
                        type: "UPDATE_TEMP_CURRENCY",
                        currency: String(key),
                      });
                      await getTempCurrencyRate(
                        String(
                          currencies.find((cur: any) => cur.code === key)?.id,
                        ),
                        projectItemCalculation.finalDate + "T00:00:00",
                      );
                      setShowLoader(false);
                    }}
                    width="100%"
                  >
                    {currencies.map((cur: any) => (
                      <Item key={cur.code}>{cur.code}</Item>
                    ))}
                  </Picker>
                  <TextField
                    label="Exchange Rate"
                    value={tempRate}
                    onChange={(value) => {
                      dispatch({ type: "UPDATE_TEMP_RATE", rate: value });
                    }}
                    width="100%"
                  />
                  <Flex direction="row" gap="size-200" justifyContent="end">
                    <Button
                      variant="secondary"
                      onPress={() => {
                        handleCancelCurrencyRate();
                        close();
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      onPress={() => {
                        handleApplyCurrencyRate();
                        close();
                      }}
                    >
                      Apply
                    </Button>
                  </Flex>
                </Flex>
              </DialogContent>
            </Dialog>
          )}
        </DialogTrigger>
      </>
    );
  }
}

export const ProjectItemGridComponent = LayoutComponent(ProjectItemGrid);
